/**
 * Раздел «Торговля» страницы «Аналитика» (US-30, задача DP-408) — новый
 * подраздел: плашка из пяти плиток (торговля общая, розничная + ИФО,
 * оптовая + ИФО, торговля за отчётный месяц, доля областного центра) и три
 * блока — «Розничные цены», «Структура оптовой торговли», «Торговля по
 * районам и городу». Существующие девять подразделов он не меняет.
 *
 * Плашка и каждый блок грузятся **своей операцией** (US-31 FR-1.1):
 * источники выходят с разной периодичностью — объём и ИФО ежемесячно
 * 12–13 числа, цены ежемесячно 9–12 числа, структура раз в год, — поэтому
 * общей отчётной подписи у страницы нет, и недоступность годового выпуска
 * не обнуляет месячную плашку.
 *
 * Плитка «Торговля (общая)» показывает ИФО, только если он пришёл из
 * ответа: экран индекс не считает ни при каких условиях (FR-8.3, открытый
 * вопрос 1 US-30 — наполнение плитки и наличие у неё ИФО). Плитка «Доля
 * областного центра» приходит только для уровня области (US-31 FR-7.4) —
 * на районе и городе её просто нет (FR-2.5.1). Показатель без данных не
 * отображается, остальные перераспределяются по ширине (FR-2.7).
 */
import { ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TradePrices } from './TradePrices';
import { TradeTerritories } from './TradeTerritories';
import { TradeWholesale } from './TradeWholesale';
import {
  PeriodCaption,
  fmt1,
  useStatBlock,
  useStatText,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { WidgetErrorBoundary } from '../../../components/common/WidgetErrorBoundary';
import { MODULES } from '../../../consts/modules';
import {
  StatCard,
  type StatCardFootnote,
} from '../../../components/widgets/StatCard';
import { useKato } from '../../../contexts/KatoContext';
import { useModulePresent } from '../../../hooks/useModulePresent';
import {
  getAnalyticsTradePlashka,
  getAnalyticsTradePrices,
  getAnalyticsTradeTerritories,
  getAnalyticsTradeWholesale,
} from '../../../requests/analytics.request';
import type {
  StatIfo,
  StatValue,
  TradePlashkaResponse,
  TradePricesResponse,
  TradeTerritoriesResponse,
  TradeWholesaleResponse,
} from '../../../types/analytics';

interface TileVM {
  key: string;
  title: string;
  value: string;
  unit: string | null;
  footnotes: StatCardFootnote[];
}

/**
 * Объём торговли выпуск ведёт в тыс. тенге — девятизначные числа в плитке
 * не читаются и расходятся по написанию с соседними разделами (млрд
 * тенге). Значение и объявленная единица пересчитываются вместе, так что
 * единица на экране остаётся верной (замечание тестирования 3.1.3).
 */
const THS_TENGE = new Set(['тыс. тенге', 'тыс. теңге', 'тыс.тенге']);

const toBlnTenge = (value: StatValue | null): StatValue | null => {
  if (value === null || value.unit === null) return value;
  if (!THS_TENGE.has(value.unit.trim())) return value;
  return {
    ...value,
    value: value.value === null ? null : value.value / 1e6,
    unit: value.unit.includes('теңге') ? 'млрд теңге' : 'млрд тенге',
  };
};

export const TradeSection = () => {
  const { t } = useTranslation();
  const { selectedKato } = useKato();
  const text = useStatText();

  // Видимость блоков задаёт супер-администратор (US-09): выключенный блок
  // не отображается вовсе и состоянием «нет данных» не подменяется
  // (FR-9.7), а остальные смыкаются (FR-1.3).
  const showPlashka = useModulePresent(MODULES.analytics.tradePlashka);
  const showPrices = useModulePresent(MODULES.analytics.tradePrices);
  const showWholesale = useModulePresent(MODULES.analytics.tradeWholesale);
  const showTerritories = useModulePresent(MODULES.analytics.tradeTerritories);

  const plashka = useStatBlock<TradePlashkaResponse>(getAnalyticsTradePlashka);
  const prices = useStatBlock<TradePricesResponse>(getAnalyticsTradePrices);
  const wholesale = useStatBlock<TradeWholesaleResponse>(
    getAnalyticsTradeWholesale,
  );
  const territories = useStatBlock<TradeTerritoriesResponse>(
    getAnalyticsTradeTerritories,
  );

  /** Подпись ИФО плитки: красная, когда динамика отрицательная (FR-5.5). */
  const ifoFootnote = (ifo: StatIfo | null): StatCardFootnote[] => {
    if (ifo === null || ifo.value === null || ifo.state !== 'value') return [];
    const grew = ifo.value >= 100;
    const good = ifo.direction === 'up_is_bad' ? !grew : grew;
    return [
      {
        text: [
          `${t('analytics.stats.ifoShort')} ${fmt1(ifo.value)}%`,
          ifo.base_label,
        ]
          .filter((part): part is string => part !== null)
          .join(' '),
        bold: ifo.direction === null || good,
        danger: ifo.direction !== null && !good,
      },
    ];
  };

  /** Подпись «розничная X · оптовая Y» — раскладка величины плитки. */
  const splitFootnote = (
    retail: StatValue | null,
    wholesaleValue: StatValue | null,
  ): StatCardFootnote[] => {
    const parts = (
      [
        ['retail', retail],
        ['wholesale', wholesaleValue],
      ] as Array<[string, StatValue | null]>
    ).flatMap(([key, raw]) => {
      const value = toBlnTenge(raw);
      if (value === null || value.state === 'no_data') return [];
      const shown = text(value, fmt1);
      return [`${t(`analytics.trade.split.${key}`)} ${shown.text}`];
    });
    return parts.length > 0 ? [{ text: parts.join(' · ') }] : [];
  };

  /** Плитка строится только при наличии величины (FR-2.7). */
  const tile = (
    key: string,
    title: string,
    value: StatValue | null,
    footnotes: StatCardFootnote[],
  ): TileVM[] => {
    value = toBlnTenge(value);
    if (value === null || value.state === 'no_data') return [];
    if (value.state === 'not_applicable') return [];
    const shown = text(value, fmt1);
    return [
      {
        key,
        title,
        value: shown.text,
        unit: shown.known ? value.unit : null,
        footnotes: shown.known ? footnotes : [],
      },
    ];
  };

  const data = plashka.data;
  const monthLabel = data?.month?.period?.label ?? null;
  const centerShare = data?.center_share ?? null;

  const tiles: TileVM[] = [
    // «Торговля (общая)»: ИФО показывается, только если пришёл из ответа —
    // взвешенного или выведенного делением индекса на экране не бывает
    // (FR-2.3, FR-8.3).
    ...tile(
      'total',
      t('analytics.trade.cards.total'),
      data?.total?.value ?? null,
      [
        ...ifoFootnote(data?.total?.ifo ?? null),
        ...(data?.total?.value?.calculated === true
          ? [{ text: t('analytics.trade.cards.totalCalculated') }]
          : []),
      ],
    ),
    ...tile(
      'retail',
      t('analytics.trade.cards.retail'),
      data?.retail?.value ?? null,
      ifoFootnote(data?.retail?.ifo ?? null),
    ),
    ...tile(
      'wholesale',
      t('analytics.trade.cards.wholesale'),
      data?.wholesale?.value ?? null,
      ifoFootnote(data?.wholesale?.ifo ?? null),
    ),
    // Величина за отчётный месяц приходит отдельным столбцом выпуска и
    // разностью накопленных периодов не считается (FR-2.4).
    ...tile(
      'month',
      monthLabel !== null
        ? t('analytics.trade.cards.month', { period: monthLabel })
        : t('analytics.trade.cards.monthPlain'),
      data?.month?.value ?? null,
      splitFootnote(data?.month?.retail ?? null, data?.month?.wholesale ?? null),
    ),
    // Доля областного центра — расчётная величина и только для области
    // (FR-2.5, FR-2.5.1).
    ...tile(
      'centerShare',
      centerShare !== null && centerShare.territory !== null
        ? t('analytics.trade.cards.centerShare', {
            territory: centerShare.territory,
          })
        : t('analytics.trade.cards.centerSharePlain'),
      centerShare?.total ?? null,
      splitFootnote(centerShare?.retail ?? null, centerShare?.wholesale ?? null),
    ),
  ];

  return (
    <section className="space-y-4" data-testid="analytics-trade">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.trade.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <ShoppingCart
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {showPlashka && (
        <WidgetErrorBoundary key={`trade-plashka-${selectedKato ?? 'none'}`}>
          {plashka.loading ? (
            <BlockLoader />
          ) : tiles.length === 0 ? (
            <p
              data-testid="block-placeholder"
              className="text-sm lowercase text-text-muted"
            >
              {t('common.noData')}
            </p>
          ) : (
            <div className="space-y-2.5" data-testid="trade-plashka">
              {/* Отчётный период плашки — накопленный с начала года; у
                  блоков ниже периоды свои (FR-6.1). */}
              <PeriodCaption
                period={plashka.data?.period ?? null}
                testId="trade-plashka-period"
              />
              <div className="flex flex-wrap gap-3.5">
                {tiles.map((card) => (
                  <div key={card.key} className="min-w-52 flex-1">
                    <StatCard
                      testId={`analytics-card-trade-${card.key}`}
                      title={card.title}
                      value={card.value}
                      unit={card.unit}
                      footnotes={card.footnotes}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </WidgetErrorBoundary>
      )}

      {showPrices && (
        <WidgetErrorBoundary key={`trade-prices-${selectedKato ?? 'none'}`}>
          <TradePrices data={prices.data} loading={prices.loading} />
        </WidgetErrorBoundary>
      )}

      {showWholesale && (
        <WidgetErrorBoundary key={`trade-wholesale-${selectedKato ?? 'none'}`}>
          <TradeWholesale data={wholesale.data} loading={wholesale.loading} />
        </WidgetErrorBoundary>
      )}

      {showTerritories && (
        <WidgetErrorBoundary key={`trade-territories-${selectedKato ?? 'none'}`}>
          <TradeTerritories
            data={territories.data}
            loading={territories.loading}
          />
        </WidgetErrorBoundary>
      )}
    </section>
  );
};
